import React, { Fragment, useEffect, useRef, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Typography } from "@mui/material";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonDialog from "../../common/components/CommonDialog";
import { Close } from "@mui/icons-material";
import { getPastSession } from "../../api/sessionApi";
import { ServiceOffer, serviceOptions } from "../../common/const";
import NoteViewer from "../../common/components/NoteViewer";

const SessionHistory = () => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [events, setEvents] = useState([]);
    const [selectedSession, setSelectedSesion] = useState(null);
    const [viewNotes, setViewNotes] = useState(false);
    const [page, setPage] = useState(1);
    const scrollRef = useRef(null);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const rowsPerPage = 10;

    const getEvents = async () => {
        try {
            if (page > 1) {
                setIsFetchingMore(true);
            } else {
                dispatch(setLoading({ loading: true }));
            }
            const response = await getPastSession(page, rowsPerPage, ServiceOffer.counsellor, user?.student_id, '');
            if (response?.data?.data.length === 0) {
                if (page === 1) {
                    setEvents([]);
                }
                setHasMore(false);
            } else {
                setEvents(prev =>
                    page === 1 ? response?.data?.data : [...prev, ...response?.data?.data]
                );
            }
            dispatch(setLoading({
                loading: false
            }));
            setIsFetchingMore(false);
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
            setIsFetchingMore(false);
        }
    }

    useEffect(() => {
        if (user?.user_id) {
            getEvents();
        }
    }, [user?.user_id, page]);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={viewNotes}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>View Summary</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setViewNotes(false);
                                    setSelectedSesion(null);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    selectedSession?.sessionNotes.map((a) => (
                        <NoteViewer note={a} />
                    ))
                }
            />
            <CommonCard
                borderRadius={2}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: '13px' }}
                        >
                            <Box>
                                <Typography fontWeight={600}>Session History</Typography>
                                <Typography fontSize={14} color={theme.palette.background.dark}>Past sessions and meeting summaries</Typography>
                            </Box>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box
                        ref={scrollRef}
                        onScroll={(e) => {
                            const { scrollTop, scrollHeight, clientHeight } = e.target;

                            const isNearBottom =
                                scrollHeight - scrollTop <= clientHeight + 50;

                            if (isNearBottom && !isFetchingMore && hasMore) {
                                setIsFetchingMore(true);   // 🔒 lock
                                setPage(prev => prev + 1);
                            }
                        }}
                        sx={{
                            height: events.length > 0 ? '40vh' : '4vh',
                            overflow: 'auto'
                        }}
                    >
                        {events.length > 0 ? events.map((a, index) => {
                            return (
                                <CommonCard
                                    key={index}
                                    background={theme.palette.background.main}
                                    borderRadius={2}
                                    paddingBottom="0px"
                                    padding="13px"
                                    marginTop={index === 0 ? "0px" : "10px"}
                                    content={
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                gap={1}
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        background: theme.palette.secondary.main,
                                                        borderRadius: 1,
                                                        padding: 1,
                                                        width: '65px',
                                                        cursor: 'pointer',
                                                        border: `1px solid ${theme.palette.background.secondary}`
                                                    }}
                                                >
                                                    <Typography fontSize={19} fontWeight={600} color={theme.palette.primary.main} textAlign="center">{moment(a.session_start_time).date()}</Typography>
                                                    <Typography fontSize={11} fontWeight={600} color="White" textAlign="center">{moment(a.session_start_time).format('MMM YYYY')}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        fontSize={{ xs: 13, sm: 14 }}
                                                        fontWeight="600"
                                                        sx={{
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        {a.topic || serviceOptions.find((i) => i.value === a.service_name)?.label}
                                                    </Typography>
                                                    <Typography fontSize={13} color={theme.palette.background.dark}>
                                                        {`${moment(a.session_start_time).format('hh:mm A')} - ${moment(a.session_end_time).format('hh:mm A')}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {a.sessionNotes?.length > 0 && (
                                                <CommonButton
                                                    marginBottom="0px"
                                                    label="View Summary"
                                                    width="120px"
                                                    fontSize="12px"
                                                    height="30px"
                                                    fontWeight="600"
                                                    color={theme.palette.background.dark}
                                                    background="Transparent"
                                                    border={`1px solid #0000001A`}
                                                    handleClick={() => {
                                                        setViewNotes(true);
                                                        setSelectedSesion(a);
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                            )
                        }) : (
                            <Typography textAlign="center" color={theme.palette.background.dark}>No Session Found</Typography>
                        )}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
                marginTop="15px"
            />
        </Fragment>
    )
}

export default SessionHistory;